import { CSSProperties } from 'react';

import { autocompleteClasses } from '@mui/material/Autocomplete';
import { inputBaseClasses } from '@mui/material/InputBase';
import { paginationItemClasses } from '@mui/material/PaginationItem';
import { createTheme } from '@mui/material/styles';
import { tabClasses } from '@mui/material/Tab';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import { pickersSlideTransitionClasses } from '@mui/x-date-pickers-pro';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

import { buttonTheme } from './theme/ButtonTheme';
import { muiCheckboxTheme } from './theme/CheckboxTheme';
import { datePickerTheme } from './theme/DatePickerTheme';
import { iconButtonTheme } from './theme/IconButtonTheme';
import { outlinedInputTheme } from './theme/OutlinedInputTheme';
import { paletteGreen, paletteGrey, paletteOrange, paletteRed } from './theme/Palette';
import { pickersOutlinedInputTheme } from './theme/PickersOutlinedInputTheme';
import { muiRadioTheme } from './theme/RadioTheme';
import { getLocalizedDayOfWeek } from '@shared/util/localizationHelper';

declare module '@mui/material/styles' {
  interface PaletteColor {
    shadow: string;
  }
  interface SimplePaletteColorOptions {
    shadow?: string;
  }
  interface TypeText {
    headingH0: string;
    headingH1: string;
    paragraphP1ColorVariantDark: string;
    paragraphP1ColorVariantRegular: string;
    paragraphP1ColorVariantLight: string;
    paragraphP2ColorVariantDark: string;
    paragraphP2ColorVariantRegular: string;
    paragraphP2ColorVariantLight: string;
  }
  interface TypographyVariants {
    h0: CSSProperties;
    p1: CSSProperties;
    p2: CSSProperties;
  }
  interface TypographyVariantsOptions {
    h0?: CSSProperties;
    p1?: CSSProperties;
    p2?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h0: true;
    p1: true;
    p2: true;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      light: paletteGreen[50],
      main: paletteGreen[600],
      dark: paletteGreen[700],
      shadow: paletteGreen[100],
    },
    secondary: {
      light: paletteGrey[50],
      main: paletteGrey[200],
      dark: paletteGrey[400],
      shadow: paletteGrey[100],
    },
    warning: {
      light: paletteOrange[50],
      main: paletteOrange[800],
      dark: paletteOrange[900],
      shadow: paletteOrange[100],
    },
    error: {
      light: paletteRed[50],
      main: paletteRed[500],
      dark: paletteRed[600],
      shadow: paletteRed[100],
    },
    action: {
      disabled: paletteGrey[200],
      disabledBackground: paletteGrey[50],
      hover: paletteGreen[700],
    },
    text: {
      disabled: paletteGrey[500],
      paragraphP1ColorVariantDark: paletteGrey[900],
      paragraphP1ColorVariantRegular: paletteGrey[700],
      paragraphP1ColorVariantLight: paletteGrey[400],
      paragraphP2ColorVariantDark: paletteGrey[900],
      paragraphP2ColorVariantRegular: paletteGrey[700],
      paragraphP2ColorVariantLight: paletteGrey[400],
    },
    grey: paletteGrey,
  },
  shape: {
    borderRadius: 6,
  },
  typography: {
    h0: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      fontSize: '32px',
      lineHeight: '40px',
      color: paletteGrey[700],
    },
    h1: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
      color: paletteGrey[700],
    },
    h3: {
      fontFamily: 'Roboto',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      color: paletteGrey[900],
    },
    p1: {
      fontFamily: 'Roboto',
      fontWeight: 300,
      fontSize: '16px',
      lineHeight: '20px',
      color: paletteGrey[600],
    },
    p2: {
      fontFamily: 'Roboto',
      fontWeight: 300,
      fontSize: '14px',
      lineHeight: '20px',
      color: paletteGrey[900],
    },
    body1: {
      fontFamily: 'Roboto',
      fontWeight: 300,
      fontSize: '16px',
      lineHeight: '20px',
      color: paletteGrey[600],
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 32,
        },
        input: {
          fontSize: 'var(--field-font-size)',
          fontWeight: 300,
          [`&.${inputBaseClasses.adornedStart}`]: {
            paddingLeft: 0,
          },
          [`&.${inputBaseClasses.disabled}::placeholder`]: {
            opacity: 1,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          minHeight: 'unset',
          [`& .${tabClasses.root}.${tabClasses.selected}`]: {
            color:
              ownerState.textColor === 'inherit' ? 'inherit' : theme.palette[ownerState.textColor ?? 'primary'].dark,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          textTransform: 'none',
          fontSize: 16,
          whiteSpace: 'nowrap',
          alignItems: 'center',
          minWidth: 120,
        }),
      },
    },
    MuiOutlinedInput: outlinedInputTheme,
    MuiButton: buttonTheme,
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          [`&.${toggleButtonClasses.selected}`]: {
            color: '#fff',
          },
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        sx: {
          zIndex: 'modal',
        },
      },
    },
    MuiRadio: muiRadioTheme,
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          width: 42,
        },
        slideTransition: {
          minHeight: 250,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          width: 355,
        },
      },
    },
    MuiDateRangeCalendar: {
      defaultProps: {
        dayOfWeekFormatter: getLocalizedDayOfWeek,
      },
      styleOverrides: {
        monthContainer: {
          '&:not(:last-of-type)': {
            borderRight: 'none !important;',
          },
          width: 355,
        },
      },
    },
    MuiDatePicker: datePickerTheme,
    MuiPickersOutlinedInput: pickersOutlinedInputTheme,
    MuiPagination: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: theme.transitions.create('all', { duration: theme.transitions.duration.shortest }),
          [`&.${paginationItemClasses.selected}`]: {
            color: '#ffffff',
          },
          [`&:not(.${paginationItemClasses.ellipsis}):hover`]: {
            color: '#fff',
          },
          [`&.${paginationItemClasses.ellipsis}`]: {
            cursor: 'default',
          },
        }),
      },
    },
    MuiIconButton: iconButtonTheme,
    MuiCheckbox: muiCheckboxTheme,
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto',
        },
      },
    },
    MuiDateRangePicker: {
      defaultProps: {
        slotProps: {
          popper: {
            placement: 'bottom-end',
          },
          layout: {
            sx: {
              [`& .${pickersSlideTransitionClasses.root}`]: {
                minHeight: '240px',
              },
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ _, theme }) => ({
          [`& .${autocompleteClasses.option}`]: {
            fontSize: 'var(--field-font-size)',
            fontWeight: 'var(--field-font-weight)',
            lineHeight: 'var(--field-line-height)',
          },
          [`& .${autocompleteClasses.option}.${autocompleteClasses.focused}`]: {
            backgroundColor: theme.palette.primary.light,
          },
        }),
      },
    },
  },
});
